import { useCallback, useEffect, useRef, useState } from 'react';

type ApiResponse<T, K extends string> = {
  data: {
    [key in K]: T;
  } & Record<string, any>;
};

type UsePaginatedApiSearchParams<T, K extends string> = {
  apiMethod: (params: any, signal?: AbortSignal) => Promise<ApiResponse<T, K>>;
  params?: Record<string, any>;
  searchKey?: string;
  responseKey: K;
  minQueryLength?: number;
  debounceDelay?: number;
  notSendRequest?: boolean;
  initQuery?: string;
  perPage?: number;
};

const initialParams = {};

const getItemKey = (item: any) => item?.value ?? item?.id ?? JSON.stringify(item);

const mergeUniqueItems = <T extends any[]>(current: T, next: T): T => {
  const existingKeys = new Set(current.map(getItemKey));
  return [
    ...current,
    ...next.filter((item) => !existingKeys.has(getItemKey(item))),
  ] as T;
};

const getHasMore = (responseData: Record<string, any>, page: number, receivedCount: number, perPage: number) => {
  const pagination = responseData?.pagination ?? responseData?._meta?.pagination ?? responseData?._meta;
  const currentPage = Number(pagination?.currentPage ?? pagination?.page ?? page);
  const totalPages = Number(pagination?.totalPages ?? pagination?.pageCount ?? pagination?.page_count);

  if (Number.isFinite(totalPages) && totalPages > 0) {
    return currentPage < totalPages;
  }

  const totalCount = Number(pagination?.totalCount ?? pagination?.total);
  if (Number.isFinite(totalCount) && totalCount >= 0) {
    return currentPage * perPage < totalCount;
  }

  return receivedCount >= perPage;
};

export const usePaginatedApiSearch = <T extends any[], K extends string = 'data'>({
  apiMethod,
  params = initialParams,
  searchKey = 'search',
  responseKey,
  minQueryLength = 0,
  debounceDelay = 500,
  notSendRequest = false,
  initQuery = '',
  perPage = 30,
}: UsePaginatedApiSearchParams<T, K>) => {
  const [data, setData] = useState<T>([] as unknown as T);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState(initQuery);
  const requestIdRef = useRef(0);
  const loadingMoreRequestRef = useRef<number | null>(null);

  const requestPage = useCallback(async (
    searchQuery: string,
    requestedPage: number,
    replace: boolean,
    signal?: AbortSignal,
  ) => {
    if (searchQuery.length < minQueryLength || notSendRequest) {
      setData([] as unknown as T);
      setHasMore(false);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    if (!replace && loadingMoreRequestRef.current !== null) return;
    const requestId = ++requestIdRef.current;
    if (replace) {
      loadingMoreRequestRef.current = null;
      setLoadingMore(false);
      setLoading(true);
    } else {
      loadingMoreRequestRef.current = requestId;
      setLoadingMore(true);
    }

    try {
      const response = await apiMethod({
        ...params,
        [searchKey]: searchQuery,
        paramsPagination: {
          ...params?.paramsPagination,
          perPage,
          page: requestedPage,
        },
      }, signal);

      if (signal?.aborted || requestId !== requestIdRef.current) return;

      const responseData = response?.data ?? {};
      const nextItems = (responseData?.[responseKey] ?? responseData ?? []) as T;
      if (!Array.isArray(nextItems)) {
        if (replace) setData([] as unknown as T);
        setHasMore(false);
        return;
      }

      if (replace) {
        setData(nextItems);
        setHasMore(getHasMore(responseData, requestedPage, nextItems.length, perPage));
      } else {
        setData((current) => {
          const merged = mergeUniqueItems(current, nextItems);
          const receivedNewItems = merged.length > current.length;
          setHasMore(receivedNewItems && getHasMore(responseData, requestedPage, nextItems.length, perPage));
          return merged;
        });
      }

      setPage(requestedPage);
    } catch (error) {
      if (signal?.aborted || requestId !== requestIdRef.current) return;
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Paginated search error:', error);
      }
      if (replace) setData([] as unknown as T);
      setHasMore(false);
    } finally {
      if (loadingMoreRequestRef.current === requestId) loadingMoreRequestRef.current = null;
      if (requestId === requestIdRef.current) {
        replace ? setLoading(false) : setLoadingMore(false);
      }
    }
  }, [apiMethod, minQueryLength, notSendRequest, params, perPage, responseKey, searchKey]);

  useEffect(() => {
    const controller = new AbortController();
    loadingMoreRequestRef.current = null;
    setLoading(true);
    setLoadingMore(false);
    setHasMore(false);
    const timeout = window.setTimeout(
      () => requestPage(query, 1, true, controller.signal),
      query ? debounceDelay : 0,
    );

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
      requestIdRef.current += 1;
    };
  }, [debounceDelay, query, requestPage]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    requestPage(query, page + 1, false);
  }, [hasMore, loading, loadingMore, page, query, requestPage]);

  const reset = useCallback(() => {
    setQuery('');
    setData([] as unknown as T);
    setPage(1);
    setHasMore(false);
  }, []);

  return {
    data,
    loading: loading || loadingMore,
    loadingMore,
    hasMore,
    searchQuery: query,
    setSearchQuery: setQuery,
    searchParams: params,
    loadMore,
    reset,
  };
};
